/**
 * Работа с Flow Executor API Authentik.
 *
 * Зачем прокси, а не прямые вызовы из браузера: состояние flow Authentik
 * держит в своей сессии по cookie. Прямые кросс-доменные вызовы означали бы
 * сторонние cookie, которые современные браузеры режут по SameSite.
 * Проксируя через свой сервер, мы получаем ту же сессию на своём домене
 * и не светим наружу структуру flow.
 *
 * Логику по-прежнему исполняет Authentik: политики паролей, блокировки,
 * MFA. Мы только отображаем то, что он просит показать.
 */

/**
 * Адрес Authentik без завершающего слеша.
 *
 * Читается из NEXT_PUBLIC_AUTHENTIK_URL, потому что ссылки на регистрацию
 * и восстановление пароля рисуются в клиентском компоненте. Секрета здесь
 * нет: это публичный адрес, который пользователь и так видит в браузере.
 */
function authentikBase(): string {
  const raw = process.env.NEXT_PUBLIC_AUTHENTIK_URL ?? '';
  // Из issuer вида https://sso.ingdg.com/application/o/<slug>/ нужен только хост.
  try {
    return new URL(raw).origin;
  } catch {
    return 'https://sso.ingdg.com';
  }
}

export const AUTHENTIK_BASE = authentikBase();

/**
 * Идентификаторы flow продукта ingdg.com.
 *
 * Оставлены как значения по умолчанию: домен, которого нет в реестре
 * `FLOWS_BY_PRODUCT`, обслуживается основным сайтом (см. `products.ts`).
 */
export const FLOWS = {
  authentication: process.env.AUTHENTIK_FLOW_AUTHENTICATION ?? 'default-authentication-flow',
  enrollment: process.env.AUTHENTIK_FLOW_ENROLLMENT ?? 'default-enrollment-flow',
  recovery: process.env.AUTHENTIK_FLOW_RECOVERY ?? 'default-recovery-flow',
} as const;

export type FlowKind = keyof typeof FLOWS;

/**
 * Flow у каждого продукта свои.
 *
 * Почему это важно, а не косметика. Внутри flow лежит стадия письма, а в ней
 * шаблон, тема, обратный адрес и ссылка подтверждения. Пока все три домена
 * ходили в `default-enrollment-flow`, человек, зарегистрировавшийся на
 * aisearch.tech, получал письмо «Подтверждение регистрации на ingdg.com» со
 * ссылкой на ingdg.com — чужой домен, чужой бренд (и учётная запись попадала
 * в группу `ingdg-users`, то есть в чужой продукт).
 *
 * Ключ — `Product.id` из реестра продуктов. Отсутствие продукта в таблице
 * означает «пользуйся ingdg-овскими»: так ведёт себя и сам реестр, где
 * неизвестный домен обслуживается основным сайтом.
 */
const FLOWS_BY_PRODUCT: Record<string, Partial<Record<FlowKind, string>>> = {
  aisearch: {
    authentication: 'aisearch-auth-en',
    enrollment: 'aisearch-enrollment-en',
    recovery: 'aisearch-recovery-en',
  },
  datahunter: {
    authentication: 'datahunter-auth',
    enrollment: 'datahunter-enrollment',
    recovery: 'datahunter-recovery',
  },
};

/**
 * Flow русской витрины AiSearch: домен `aisearch.ru` и его поддомены.
 *
 * Одного продукта мало, потому что у AiSearch две витрины — международная
 * на `.tech` и русская на `.ru`, — и это разные домены с разной подписью.
 * Письмо лежит внутри flow, поэтому общий flow означал бы письмо не того
 * бренда: человек регистрировался на русской витрине, а получал
 * «Confirm your email» от `help@aisearch.tech`.
 *
 * Здесь перечислены только те виды, для которых заведён отдельный flow.
 * Для остальных действует общий набор продукта.
 *
 * Прежде здесь было написано, что у восстановления «письмо уже русское».
 * Это оказалось неправдой и стоило нам сломанного восстановления пароля
 * на боевом домене: письмо приходило английское и уводило на .tech.
 */
const FLOWS_AISEARCH_RU: Partial<Record<FlowKind, string>> = {
  enrollment: 'aisearch-ru-enrollment',
  /*
    Вход русской витрины.

    Без этой строки .ru брал вход из набора продукта, то есть английский
    `aisearch-auth-en` с заголовком «Welcome back». Получалась витрина
    наполовину: страницы, регистрация и восстановление по-русски, а дверь
    в кабинет — по-английски.

    Русский поток `ai-tech-ru-auth` («Авторизация») существовал давно и
    уже обслуживает соседние приложения (`2026-ai-ru`, `dev-ai-ru`).
    Состав стадий у него тот же, что у английского: опознание, пароль,
    вход. Две последние стадии вообще общие, это буквально одни и те же
    объекты, так что вход работает той же механикой.

    Отличие одно и нужное: своя стадия опознания. Она отправляет за
    регистрацией в русский поток, а не в английский.
  */
  authentication: 'ai-tech-ru-auth',
  /*
    Восстановление пароля русской витрины.

    Пока этой строки не было, aisearch.ru брал восстановление из набора
    продукта, то есть английский `aisearch-recovery-en`. А в его шаблоне
    домен записан прямо в разметке (`https://aisearch.tech/auth/reset`),
    не переменной. Поэтому человек, забывший пароль на русском сайте,
    получал письмо на английском и ссылку на чужой домен: переходил,
    видел международную витрину и уходил.

    Нашли на боевом домене 22.09.2026, сразу после переезда. На стенде
    было то же самое, просто туда никто не ходил забывать пароль.

    Русский поток в Authentik существовал всё это время
    (`ai-tech-ru-recovery-password`, шаблон `recovery-ai-tech-ru.html`),
    и ссылку он берёт из переменной `{{ url }}`, то есть домен
    подставляет сам Authentik по запросу. Не хватало ровно этой строки.
  */
  recovery: 'ai-tech-ru-recovery-password',
};

/**
 * Flow нужного вида для продукта.
 *
 * `host` необязателен: он нужен там, где у одного продукта несколько витрин
 * с разной подписью. Без него возвращается набор продукта, и это верное
 * поведение для вызовов, где домена нет (например, юнит-тест).
 */
export function flowForProduct(productId: string, kind: FlowKind, host?: string | null): string {
  if (productId === 'aisearch' && ruDomenAisearch(host)) {
    const ruFlow = FLOWS_AISEARCH_RU[kind];
    if (ruFlow) return ruFlow;
  }

  return FLOWS_BY_PRODUCT[productId]?.[kind] ?? FLOWS[kind];
}

/**
 * Домен русской витрины AiSearch.
 *
 * Правило намеренно совпадает с `tolkoRusskij` из `ais-ru-domen.ts`:
 * витрина и её flow обязаны определяться одинаково, иначе человек увидит
 * русскую страницу, а письмо получит английское. Своя копия здесь, а не
 * импорт, потому что этот модуль читают и клиентские компоненты, а тот
 * тянет `next/headers`.
 *
 * Суффикс, а не «содержит»: `evilaisearch.ru` не должен считаться своим.
 */
export function ruDomenAisearch(host: string | null | undefined): boolean {
  if (!host) return false;

  const hostname = host.split(':')[0]?.toLowerCase() ?? '';
  return hostname === 'aisearch.ru' || hostname.endsWith('.aisearch.ru');
}

/** Ошибка поля, как её присылает Authentik. */
export interface FieldError {
  string: string;
  code: string;
}

/**
 * Ответ стадии. Набор полей зависит от компонента, поэтому описаны только те,
 * что мы реально используем, остальное остаётся доступным через индекс.
 */
export interface FlowChallenge {
  component: string;
  flow_info?: { title?: string };
  response_errors?: Record<string, FieldError[]>;

  // ak-stage-identification
  user_fields?: string[];
  password_fields?: boolean;
  primary_action?: string;
  captcha_stage?: unknown;
  sources?: unknown[];
  enable_remember_me?: boolean;

  // ak-stage-password
  pending_user?: string;
  pending_user_avatar?: string;
  recovery_url?: string;

  // ak-stage-prompt: набор полей задаётся на стороне Authentik
  fields?: PromptField[];

  // ak-stage-email: письмо отправлено, ждём перехода по ссылке
  email_sent?: boolean;

  // ak-stage-consent: приходит при возврате по ссылке из письма.
  // Обязательная стадия, её нельзя отключить: защита от почтовых сканеров,
  // которые открывают ссылки за пользователя.
  header_text?: string;
  permissions?: unknown[];
  additional_permissions?: unknown[];
  /** Токен согласия. Это НЕ тот токен, что пришёл в письме. */
  token?: string;

  // xak-flow-redirect
  to?: string;

  // ak-stage-access-denied: причина отказа, например «Неверный пароль»
  error_message?: string;

  [key: string]: unknown;
}

/** Поле формы, описанное Authentik в стадии ak-stage-prompt. */
export interface PromptField {
  field_key: string;
  label: string;
  type: string;
  required: boolean;
  placeholder?: string;
  initial_value?: string;
  sub_text?: string;
}

/**
 * Компоненты, которые мы умеем рисовать сами.
 *
 * Для всего остального интерфейс честно уходит на страницу Authentik:
 * если завтра включат MFA или капчу, пользователь увидит рабочую форму,
 * а не белый экран.
 */
export const SUPPORTED_COMPONENTS = new Set([
  'ak-stage-identification',
  'ak-stage-password',
  'ak-stage-prompt',
  'ak-stage-email',
  'ak-stage-consent',
  'xak-flow-redirect',
]);

export function isSupported(component: string): boolean {
  return SUPPORTED_COMPONENTS.has(component);
}

/**
 * Authentik оборвал flow отказом.
 *
 * Так заканчивается пятый неверный пароль подряд: вместо новой попытки
 * приходит `ak-stage-access-denied` с `error_message: "Неверный пароль"`.
 * Раньше форма принимала любую незнакомую стадию за конец входа, звала
 * complete, получала 403 и писала «Пароль верный, но доступ не открыт».
 * Люди с неверным паролем писали в поддержку, а не шли восстанавливать.
 */
export function isAccessDenied(component: string): boolean {
  return component === 'ak-stage-access-denied';
}

/**
 * Стадия, с которой flow начинается заново.
 *
 * Нужна для возврата по ссылке из письма: если вместо продолжения пришёл
 * первый шаг, значит токен уже израсходован. Ссылка одноразовая, и повторное
 * открытие письма приводит именно к этому.
 */
export function isFirstStage(component: string): boolean {
  return component === 'ak-stage-prompt' || component === 'ak-stage-identification';
}

/** Полный URL executor для указанного flow. */
export function executorUrl(flow: string): string {
  return `${AUTHENTIK_BASE}/api/v3/flows/executor/${flow}/?query=`;
}

/** Страница Authentik для запасного пути. */
export function fallbackUrl(flow: string): string {
  return `${AUTHENTIK_BASE}/if/flow/${flow}/`;
}

/**
 * URL executor для возврата по ссылке из письма.
 *
 * Токен передаётся внутри параметра query как urlencoded querystring:
 * ?query=flow_token%3D<token>. Первым ответом придёт ak-stage-consent.
 */
export function executorUrlWithToken(flow: string, token: string): string {
  const inner = new URLSearchParams({ flow_token: token }).toString();
  return `${AUTHENTIK_BASE}/api/v3/flows/executor/${flow}/?query=${encodeURIComponent(inner)}`;
}
